/**
 * Business Environment Factor Edit Route
 *
 * Edit an existing business environment factor using BusinessEnvironmentFactorForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useBusinessEnvironmentFactor } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { BusinessEnvironmentFactorForm } from '../../components/business-environment-factors';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import {
  updateBusinessEnvironmentFactor,
  type UpdateBusinessEnvironmentFactorInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreateBusinessEnvironmentFactorFormData } from '../../components/forms';
import './edit.css';

export const Route = createFileRoute(
  '/business-environment-factors/$businessEnvironmentFactorId/edit'
)({
  component: BusinessEnvironmentFactorEditPage,
});

function BusinessEnvironmentFactorEditPage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const { businessEnvironmentFactorId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch business environment factor data
  const {
    data,
    loading,
    error: fetchError,
  } = useBusinessEnvironmentFactor(businessEnvironmentFactorId);
  const businessEnvironmentFactor = data?.businessEnvironmentFactor;

  if (loading) {
    return (
      <div className="business-environment-factor-edit-page">
        <LoadingSpinner centered message="Loading business environment factor..." />
      </div>
    );
  }

  if (fetchError || !businessEnvironmentFactor) {
    return (
      <div className="business-environment-factor-edit-page">
        <EmptyState
          icon="error"
          title="Failed to load business environment factor"
          description={fetchError?.message || 'Business environment factor not found'}
          actionLabel="Back to Business Environment Factors"
          onAction={() =>
            navigate({ to: '/business-environment-factors', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  const handleSubmit = async (formData: CreateBusinessEnvironmentFactorFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build update input
      const updates: UpdateBusinessEnvironmentFactorInput = {
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category || undefined,
        label: formData.label || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      await updateBusinessEnvironmentFactor(businessEnvironmentFactorId, updates);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['GetBusinessEnvironmentFactor', 'ListBusinessEnvironmentFactors'],
      });

      // Navigate back to business environment factor detail page
      navigate({ to: `/business-environment-factors/${businessEnvironmentFactorId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: `/business-environment-factors/${businessEnvironmentFactorId}` as string });
  };

  // Map business environment factor data to form default values
  const defaultValues: Partial<CreateBusinessEnvironmentFactorFormData> = {
    name: businessEnvironmentFactor.name,
    description: businessEnvironmentFactor.description || undefined,
    category: businessEnvironmentFactor.category || undefined,
    label: businessEnvironmentFactor.label || undefined,
    tags: businessEnvironmentFactor.tags || [],
    notes: businessEnvironmentFactor.notes || undefined,
  };

  return (
    <div className="business-environment-factor-edit-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Business Environment Factor</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update business environment factor information
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <BusinessEnvironmentFactorForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
